import React, { useState, useEffect, useRef } from "react";
import { recordBannerAction } from "../services/bannerService";
import "./BannerWizard.css";

const BannerWizard = ({ banners, onDone }) => {

  const [bannerIndex, setBannerIndex] = useState(0);
  const [pageIndex, setPageIndex] = useState(
    banners[0]?.currentPage || 0
  );

  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  // 🔥 lock scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  // 🔥 dynamic scale (like preview modal)
  useEffect(() => {
    const updateScale = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();

      const maxW = rect.width * 0.9;
      const maxH = rect.height * 0.8;

      const scaleX = maxW / 1280;
      const scaleY = maxH / 720;

      setScale(Math.min(scaleX, scaleY));
    };

    updateScale();
    window.addEventListener("resize", updateScale);

    return () => window.removeEventListener("resize", updateScale);
  }, []);

  if (!banners || banners.length === 0) return null;

  const current = banners[bannerIndex];
  const banner = current.banner;
  const pages = banner.pages || [];

  const isLastPage = pageIndex >= pages.length - 1;
  const isLastBanner = bannerIndex >= banners.length - 1;

  const goNextBanner = () => {
    if (!isLastBanner) {
      setBannerIndex(i => i + 1);
      setPageIndex(banners[bannerIndex + 1]?.currentPage || 0);
    } else {
      onDone();
    }
  };

  const handleRead = async () => {
    try {
      await recordBannerAction(banner.id, "read", pageIndex);
    } catch (e) {
      console.error(e);
    }
    goNextBanner();
  };

  const handleRemindLater = async () => {
    try {
      await recordBannerAction(banner.id, "remind_later", pageIndex);
    } catch (e) {
      console.error(e);
    }
    goNextBanner();
  };

  const handleNotInterested = async () => {
    try {
      await recordBannerAction(banner.id, "not_interested", pageIndex);
    } catch (e) {
      console.error(e);
    }
    goNextBanner();
  };

  const handleNext = () => {
    if (!isLastPage) setPageIndex(i => i + 1);
  };

  const handlePrev = () => {
    if (pageIndex > 0) setPageIndex(i => i - 1);
  };

  // The banner's call to action, when the dashboard gave it one. Until now the
  // label and route were stored but never shown, so no banner button ever
  // reached a customer.
  const action =
    banner.action?.label && banner.action?.route ? banner.action : null;

  // Recorded as "clicked" first — the dashboard's banner stats count it — then
  // followed with a full navigation, the same way notification actions go.
  // The route may be a /go/ link that has to pick a channel on arrival, and
  // the wizard's scroll lock is left behind with the old page.
  const handleAction = async () => {
    try {
      await recordBannerAction(banner.id, "clicked", pageIndex);
    } catch (e) {
      console.error(e);
    }
    window.location.assign(action.route);
  };

  return (
    <div className="bw-root">

      <div className="bw-backdrop" />

      <div className="bw-stage" ref={containerRef}>

        {/* 🔥 SCALE SYSTEM */}
        <div
          className="bw-wrapper"
          style={{
            width: 1280 * scale,
            height: 720 * scale
          }}
        >
          <div
            className="bw-screen"
            style={{
              width: 1280,
              height: 720,
              transform: `scale(${scale})`,
              transformOrigin: "top left"
            }}
          >
            <div
              className="bw-content"
              dangerouslySetInnerHTML={{
                __html: pages[pageIndex]?.bodyHtml
              }}
            />
          </div>
        </div>

        {/* FOOTER */}
        <div className="bw-footer">

          <div className="bw-left">
            <button className="bw-btn remind" onClick={handleRemindLater}>
              Remind Me Later
            </button>

            <button className="bw-btn not-interested" onClick={handleNotInterested}>
              Not Interested
            </button>
          </div>

          <div className="bw-right">

            {pageIndex > 0 && (
              <button className="bw-btn secondary" onClick={handlePrev}>
                Prev
              </button>
            )}

            {/* On the last page, after the customer has read what it is for. */}
            {isLastPage && action && (
              <button className="bw-btn primary" onClick={handleAction}>
                {action.label}
              </button>
            )}

            {!isLastPage ? (
              <button className="bw-btn primary" onClick={handleNext}>
                Next
              </button>
            ) : (
              <button className="bw-btn success" onClick={handleRead}>
                Mark as Read
              </button>
            )}

          </div>

        </div>

      </div>

    </div>
  );
};

export default BannerWizard;